import {
  KYC_STATUS,
  SCREENING_STATUS,
  KYC_DOC_STATUS,
  CLIENT_CLASSIFICATION,
  REQUIRED_DOCS_INDIVIDUAL,
  REQUIRED_DOCS_CORPORATE,
  KYC_DOC_TYPES,
} from "./kycConstants";

const ID_DOC_TYPES = new Set([
  KYC_DOC_TYPES.PASSPORT,
  KYC_DOC_TYPES.NATIONAL_ID,
  KYC_DOC_TYPES.DRIVERS_LICENSE,
]);

/**
 * Returns checklist items for compliance UI and approval gate.
 */
export function buildComplianceChecklist(profile, documents = [], screening = null, contract = null) {
  const isCorporate = profile?.classification === CLIENT_CLASSIFICATION.CORPORATE;
  const requiredTypes = isCorporate ? REQUIRED_DOCS_CORPORATE : REQUIRED_DOCS_INDIVIDUAL;

  const hasIdentityDoc =
    documents.some(
      (d) => ID_DOC_TYPES.has(d.doc_type) && d.status === KYC_DOC_STATUS.VALID
    ) ||
    !!(profile?.users_document_url) ||
    documents.some(
      (d) =>
        ID_DOC_TYPES.has(d.doc_type) &&
        d.storage_url &&
        d.status !== KYC_DOC_STATUS.MISSING &&
        d.id !== "required_identity"
    );
  const hasRequiredType = (type) =>
    documents.some((d) => d.doc_type === type && d.status !== KYC_DOC_STATUS.MISSING);

  const missingRequired = requiredTypes.filter((t) => !hasRequiredType(t) && !hasIdentityDoc);

  const expiredDocs = documents.filter((d) => d.status === KYC_DOC_STATUS.EXPIRED);
  const missingDocs = documents.filter((d) => d.status === KYC_DOC_STATUS.MISSING);
  const needsReviewDocs = documents.filter((d) => d.status === KYC_DOC_STATUS.NEEDS_REVIEW);

  const screeningOk =
    screening?.status === SCREENING_STATUS.CLEAR ||
    screening?.status === SCREENING_STATUS.MANUAL_REVIEW;

  const contractFields = contract || profile?.contract_summary || {};
  const contractComplete =
    contractFields.signature_date ||
    contractFields.contract_open_date ||
    contractFields.signature_status === "needs_review";
  const hasContractWallet =
    contractFields.contract_wallet || contractFields.contract_wallet_status === "needs_review";
  const hasFidduxWallet =
    contractFields.fiddux_wallet_disclosed ||
    contractFields.fiddux_wallet_status === "needs_review";

  const items = [
    {
      id: "identity",
      label: "Identity document verified",
      ok: hasIdentityDoc,
      severity: "critical",
    },
    {
      id: "screening",
      label: "PEP / sanctions screening resolved",
      ok: screeningOk && screening?.status !== SCREENING_STATUS.PENDING,
      severity: "critical",
    },
    {
      id: "expired",
      label: "No expired documents",
      ok: expiredDocs.length === 0,
      severity: "high",
      count: expiredDocs.length,
    },
    {
      id: "missing",
      label: "No missing required documents",
      ok: missingDocs.length === 0 && missingRequired.length === 0,
      severity: "high",
      count: missingDocs.length + missingRequired.length,
    },
    {
      id: "contract",
      label: "Contract signature date recorded",
      ok: !!contractComplete,
      severity: "medium",
    },
    {
      id: "contract_wallet",
      label: "Contract wallet recorded",
      ok: !!hasContractWallet,
      severity: "medium",
    },
    {
      id: "fiddux_wallet",
      label: "Fiddux wallet disclosed at contract",
      ok: !!hasFidduxWallet,
      severity: "medium",
    },
    {
      id: "review_queue",
      label: "No documents pending review",
      ok: needsReviewDocs.length === 0,
      severity: "low",
      count: needsReviewDocs.length,
    },
  ];

  if (isCorporate) {
    items.push({
      id: "ubo",
      label: "UBO / corporate registration on file",
      ok: hasRequiredType(KYC_DOC_TYPES.CORPORATE_REGISTRATION) || hasRequiredType(KYC_DOC_TYPES.CORPORATE_ARTICLES),
      severity: "high",
    });
  }

  return items;
}

export function canApproveKyc(profile, documents = [], screening = null, contract = null) {
  const checklist = buildComplianceChecklist(profile, documents, screening, contract);
  const blockers = checklist.filter((i) => i.severity === "critical" && !i.ok);
  return {
    allowed: blockers.length === 0,
    blockers,
    checklist,
  };
}

export function computeCompletenessScore(checklist) {
  if (!checklist?.length) return 0;
  const ok = checklist.filter((i) => i.ok).length;
  return Math.round((ok / checklist.length) * 100);
}

export function deriveKycStatusFromDocs(profile, documents = [], screening = null) {
  const expired = documents.some((d) => d.status === KYC_DOC_STATUS.EXPIRED);
  const hasIdentity =
    !!(profile?.users_document_url) ||
    documents.some(
      (d) =>
        ID_DOC_TYPES.has(d.doc_type) &&
        d.status !== KYC_DOC_STATUS.MISSING &&
        d.id !== "required_identity"
    );
  const missing =
    !hasIdentity ||
    documents.some((d) => d.status === KYC_DOC_STATUS.MISSING && d.id !== "required_identity");
  const needsReview = documents.some((d) => d.status === KYC_DOC_STATUS.NEEDS_REVIEW);

  if (profile?.kyc_status === KYC_STATUS.REJECTED) return KYC_STATUS.REJECTED;
  if (expired) return KYC_STATUS.EXPIRED;
  if (missing) return KYC_STATUS.MISSING_DOCS;
  if (needsReview || screening?.status === SCREENING_STATUS.PENDING) return KYC_STATUS.IN_REVIEW;
  if (profile?.kyc_status === KYC_STATUS.APPROVED) return KYC_STATUS.APPROVED;
  return KYC_STATUS.DRAFT;
}

/** Default review interval by risk (months) */
export function nextReviewDateFromRisk(riskRating, fromDate = new Date()) {
  const months = riskRating === "high" ? 6 : riskRating === "medium" ? 12 : 24;
  const d = new Date(fromDate);
  d.setMonth(d.getMonth() + months);
  return d;
}
